// @ts-strict-ignore
import React, { ForwardedRef, forwardRef, memo, useState } from 'react'
import { IconButton, DialogTitle, DialogContent, DialogActions, Button, Tooltip, ButtonProps } from '@mui/material'
import Condition from 'components/Condition'
import ResponsiveDialog from 'components/ResponsiveDialog'
import { MoreHoriz } from '@mui/icons-material'

export interface IMoreButtonDialogProps {
  icon?: React.ReactNode
  dialogCloseButtonLabel?: React.ReactNode
  onOpen?: () => void
  onClose?: () => void
  label?: React.ReactNode
  tooltip?: React.ReactNode
  component?: React.ElementType
  dialogTitle?: string
  CloseButtonProps?: ButtonProps & { [key: string]: any }
  additionalActions?: React.ReactNode
  iconOnly?: boolean
  className?: string
  style?: React.CSSProperties
  color?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning'
  children?: React.ReactNode | ((props: { closeDialog: () => void, dialogTitle: string, dialogCloseButtonLabel: React.ReactNode }) => React.ReactNode)
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false
}

function MoreButtonDialog(props: IMoreButtonDialogProps, ref: ForwardedRef<HTMLButtonElement>) {
  const {
    icon = (<MoreHoriz />),
    dialogCloseButtonLabel = 'Close',
    onOpen,
    onClose,
    label,
    tooltip,
    component: ComponentConstructor = IconButton,
    dialogTitle,
    CloseButtonProps,
    children,
    additionalActions,
    ...restProps
  } = props

  const [isDialogOpen, setDialogOpen] = useState(false)

  const handleOpen = () => {
    setDialogOpen(true)
    if (onOpen) onOpen()
  }

  const handleClose = () => {
    setDialogOpen(false)
    if (onClose) onClose()
  }

  return (
    <React.Fragment>
      {tooltip && (
      <Tooltip title={tooltip}>
        <ComponentConstructor ref={ref} {...restProps} onClick={handleOpen}>
          {icon} {label}
        </ComponentConstructor>
      </Tooltip>
      )}

      {!tooltip && (
      <ComponentConstructor ref={ref} {...restProps} onClick={handleOpen}>
        {icon} {label}
      </ComponentConstructor>
      )}

      <Condition condition={isDialogOpen}>
        <ResponsiveDialog
          open
          onClose={handleClose}
        >
          {typeof children === 'function' ? (
            children({
              closeDialog: handleClose,
              dialogTitle: dialogTitle,
              dialogCloseButtonLabel: dialogCloseButtonLabel,
            })
          ) : (
            <React.Fragment>
              <DialogTitle>
                {dialogTitle}
              </DialogTitle>

              <DialogContent>
                {children}
              </DialogContent>

              <DialogActions>
                <Button onClick={handleClose} {...CloseButtonProps}>
                  {dialogCloseButtonLabel}
                </Button>

                {additionalActions}
              </DialogActions>
            </React.Fragment>
          )}
        </ResponsiveDialog>
      </Condition>
    </React.Fragment>
  )
}

export default memo(forwardRef(MoreButtonDialog))
